import { z } from "zod";
import {
  behaviorOfType,
  editorialClosingThemeSchema,
  formatPrice,
  parsePriceToCents,
  productInputSchema,
  slugify,
  type EditorialCalloutType,
  type EditorialContent,
  type EditorialIcon,
  type EditorialTheme,
  type Locale,
  type Product,
  type ProductInput,
  type ProductStatus,
  type ProductUpdate,
} from "@nh/shared";

/**
 * ═══════════════════════════════════════════════════════════════════
 * El formulario del producto: de lo que se escribe a lo que se guarda
 *
 * Los CAMPOS del formulario son todos texto (o casillas), y su forma
 * imita la del contrato (`translations.es.name`, `prices.MXN`,
 * `editorial.es.pillars.items.0.title`). No es capricho:
 *
 *  · Texto porque eso es lo que un `<input>` produce. Un formulario que
 *    finge tener números acaba mandando `NaN` la primera vez que alguien
 *    borra el contenido de un campo.
 *  · Con la MISMA forma que el contrato porque el esquema de abajo
 *    termina en `.pipe(productInputSchema)`: cuando el contrato rechaza
 *    algo, la ruta del error (`["translations","es","name"]`) coincide
 *    letra por letra con el nombre del campo, react-hook-form lo pinta
 *    en el control correcto sin tabla de traducción de por medio, y
 *    `productSteps.ts` puede además averiguar A QUÉ PASO pertenece ese
 *    campo para devolver a quien edita hasta él.
 *
 * Y los PRECIOS se escriben en pesos y se guardan en centavos. La
 * conversión ocurre aquí, una sola vez, con `parsePriceToCents` del
 * contrato. Ningún componente de esta carpeta ve un decimal.
 *
 * ── Las dos excepciones a "misma forma que el contrato" ─────────────
 *  · Las LISTAS son `{ value }[]` y no `string[]` (`benefits`,
 *    `imageUrls`). `useFieldArray` necesita objetos para poder dar a
 *    cada fila una `key` estable; con primitivos, borrar la fila 2 de
 *    cinco corre los valores hacia arriba y en pantalla desaparece la 5.
 *  · `currencies` no existe en el contrato. Es la LISTA de monedas
 *    activas del producto, y existe porque el mapa de precios tiene las
 *    llaves abiertas: sin ella, "qué monedas tiene este producto" solo
 *    se podría deducir de qué campos están llenos, y una moneda recién
 *    añadida y todavía vacía desaparecería al cambiar de paso.
 * ═══════════════════════════════════════════════════════════════════
 */

// ── Los valores tal y como viven en los controles ───────────────────

/** Una fila de una lista de texto suelto: beneficios, direcciones de foto. */
export interface TextItemValues {
  value: string;
}

export interface ProductContentFormValues {
  name: string;
  tagline: string;
  description: string;
  /** La leyenda de la ficha, en el idioma que se está editando. */
  disclaimer: string;
  /*
   * Los BENEFICIOS ya no están aquí. Eran texto libre por idioma —dos
   * listas paralelas que podían acabar con distinto número de renglones—
   * y ahora se eligen de un catálogo, en `ProductFormValues.benefits`,
   * una sola vez para los dos idiomas.
   *
   * Su TEXTO sigue viviendo en `translations.<idioma>.benefits` dentro
   * del documento, pero lo escribe el SERVIDOR resolviendo el catálogo,
   * así que el formulario ni lo lee ni lo manda. Ver el contrato.
   */
}

/**
 * Una VARIANTE o costo adicional del producto.
 *
 * `value` apunta al catálogo —de ahí salen el nombre y la descripción, en
 * los dos idiomas— y `amounts` es lo único que es de este producto: lo
 * que suma, por moneda y escrito en pesos como todo el dinero del
 * formulario.
 */
export interface ExtraCostValues {
  value: string;
  amounts: Record<string, string>;
}

/** Una línea de la composición de un paquete. */
export interface PackageItemValues {
  productId: string;
  /** Texto, como todo en el formulario: un `<input>` produce texto. */
  quantity: string;
}

export interface CurrencyRowValues {
  /** Código ISO 4217 en mayúsculas: `"MXN"`. */
  code: string;
}

export interface EditorialPillarValues {
  icon: string;
  title: string;
  description: string;
}

export interface EditorialIngredientValues {
  icon: string;
  name: string;
  spec: string;
  description: string;
}

export interface EditorialSpecRowValues {
  name: string;
  dose: string;
  unit: string;
  function: string;
}

export interface EditorialStatValues {
  number: string;
  label: string;
  description: string;
}

export interface EditorialSideValues {
  icon: string;
  title: string;
  description: string;
}

export interface EditorialFormValues {
  badgeLabel: string;
  compareAt: Record<string, string>;
  metadata: { content: string; dose: string; duration: string; cert: string };
  pillars: { title: string; intro: string; items: EditorialPillarValues[] };
  ingredients: {
    title: string;
    intro: string;
    theme: string;
    items: EditorialIngredientValues[];
  };
  specTable: {
    title: string;
    type: string;
    footer: string;
    items: EditorialSpecRowValues[];
  };
  process: { title: string; intro: string; items: EditorialPillarValues[] };
  stats: { title: string; intro: string; items: EditorialStatValues[] };
  sideBySide: {
    title: string;
    theme: string;
    left: EditorialSideValues;
    right: EditorialSideValues;
  };
  callout: { type: string; icon: string; title: string; body: string };
  closing: { quote: string; theme: string };
}

export interface ProductFormValues {
  slug: string;
  translations: Record<Locale, ProductContentFormValues>;

  category: string;
  type: string;
  sortOrder: string;
  /**
   * El estado del producto. YA NO tiene control propio en el formulario:
   * lo maneja `activation`. Sigue en los valores porque es donde se
   * conserva el tercer estado —`archived`— que la selección múltiple no
   * puede representar. Ver `ACTIVATION_OPTIONS`.
   */
  status: string;
  /**
   * Las cuatro opciones de activación, en UN campo.
   *
   * No son cuatro booleanos sueltos porque no se leen como cuatro
   * ajustes independientes: son las cuatro caras de una misma decisión
   * —"cómo se comporta este producto en la tienda"— y Anahí pidió
   * expresamente un selector múltiple.
   *
   * Los cuatro valores YA EXISTEN en el contrato con otros nombres. Este
   * campo no crea datos nuevos: los agrupa para editarlos y los reparte
   * al enviar. Ver `ACTIVATION_OPTIONS`.
   */
  activation: string[];
  /** La composición, cuando el tipo se comporta como paquete. */
  items: PackageItemValues[];
  /**
   * Los beneficios elegidos del catálogo, EN ORDEN. Cada fila guarda el
   * `value` de una opción; el texto lo resuelve el servidor.
   *
   * Es `{ value }[]` y no `string[]` por lo mismo que las demás listas:
   * `useFieldArray` necesita objetos para dar a cada fila una `key`
   * estable, y con primitivos borrar la fila 2 de cinco corre los valores
   * hacia arriba y en pantalla desaparece la 5.
   */
  benefits: TextItemValues[];
  /** Variantes y costos adicionales. */
  extraCosts: ExtraCostValues[];

  currencies: CurrencyRowValues[];
  prices: Record<string, string>;
  memberPrices: Record<string, string>;
  /*
   * `promoPrices` ya no es un campo. El precio en promoción se DERIVA del
   * descuento y lo escribe el servidor; el paso lo enseña de solo lectura
   * con `DerivedValue`. Tener el porcentaje y el precio escritos a la vez
   * era tener dos versiones de la misma rebaja, y podían contradecirse.
   *
   * El de FRANQUICIATARIO tampoco está, por lo mismo: sale de
   * `franchiseeDiscountPercent` aplicado al precio de socio.
   */
  compareAtPrices: Record<string, string>;
  discount: { active: boolean; type: string; value: string };

  volumePoints: string;
  compensation: {
    commissionPoints: string;
    promoVolumePoints: string;
    promoCommissionPoints: string;
    isQualifying: boolean;
    generatesPersonalPV: boolean;
    generatesGroupPV: boolean;
    /*
     * `qualifiesForRank` NO está aquí: lo lleva `activation`, bajo la
     * etiqueta "Paquete calificador". Tenerlo en los dos sitios habría
     * sido dos controles sobre el mismo dato, y esos dos controles se
     * desincronizan en cuanto alguien toca uno y guarda desde el otro.
     */
    qualifiesForText: string;
    isRepurchase: boolean;
    maintainsActive: boolean;
    membershipBenefitsText: string;
  };

  imageUrl: string;
  imageUrls: TextItemValues[];
  datasheet: { url: string; name: string };
  /** Los datos de etiqueta (`productFactsSchema`), como texto. */
  facts: {
    sku: string;
    format: string;
    servingSize: string;
    servingsPerContainer: string;
    suggestedUse: string;
    manufacturedIn: string;
    manufacturedFor: string;
    certifications: TextItemValues[];
    ingredients: TextItemValues[];
    contraindications: TextItemValues[];
  };

  stock: string;
  shipping: {
    /*
     * `requiresShipping` tampoco está aquí: lo lleva `activation`, bajo
     * la etiqueta "Requiere envío". El paso de envío sigue enseñando las
     * medidas solo cuando está puesto — lo lee de `activation`.
     */
    weightGrams: string;
    lengthCm: string;
    widthCm: string;
    heightCm: string;
  };

  validity: { from: string; to: string; durationDays: string };

  isWholesale: boolean;
  franchiseeDiscountPercent: string;
  presentation: string;
  dosage: string;
  legend: string;

  editorial: Record<Locale, EditorialFormValues>;
}

// ── Las cuatro opciones de activación ───────────────────────────────

/**
 * ═══════════════════════════════════════════════════════════════════
 * De las cuatro etiquetas de Anahí a los cuatro campos del contrato.
 *
 * Ninguno de los cuatro es nuevo. Estaban repartidos por tres pasos
 * distintos con otros nombres, y esa dispersión era el problema: nadie
 * podía ver de un vistazo cómo se comporta un producto en la tienda.
 *
 *   Activo en catálogo      →  status  ("published" / "draft")
 *   Disponible para comprar →  isAvailable
 *   Paquete calificador     →  compensation.qualifiesForRank
 *   Requiere envío          →  shipping.requiresShipping
 *
 * ── El tercer estado, que es donde esto se rompe ────────────────────
 * `status` tiene TRES valores —`draft`, `published`, `archived`— y una
 * casilla solo tiene dos. Traducir la casilla a `published`/`draft` a
 * secas convertiría cada guardado de un producto ARCHIVADO en una
 * resurrección silenciosa: se corrige una falta de ortografía y el
 * producto retirado vuelve a la tienda, o peor, se queda en borrador y
 * deja de estar archivado sin que nadie lo pidiera.
 *
 * La regla que se aplica: la opción alterna entre `published` y el
 * ESTADO DE PARTIDA cuando ese estado no es `published`. Un archivado
 * que se desmarca sigue archivado; un borrador que se desmarca sigue en
 * borrador. Y archivar o desarchivar se hace donde siempre se ha hecho
 * —desde el listado, con su diálogo—, que es una decisión con
 * consecuencias y merece una confirmación, no una casilla en un paso.
 * ═══════════════════════════════════════════════════════════════════
 */
export const ACTIVATION_OPTIONS = {
  /** `status === "published"`. */
  catalogActive: "catalogActive",
  /** `isAvailable`. */
  purchasable: "purchasable",
  /** `compensation.qualifiesForRank`. */
  qualifying: "qualifying",
  /** `shipping.requiresShipping`. */
  requiresShipping: "requiresShipping",
} as const;

export type ActivationOption =
  (typeof ACTIVATION_OPTIONS)[keyof typeof ACTIVATION_OPTIONS];

/** Si la opción está puesta en el campo múltiple. */
export function isActive(activation: readonly string[], option: ActivationOption): boolean {
  return activation.includes(option);
}

/**
 * Las opciones que corresponden a un producto GUARDADO.
 *
 * Es la mitad de ida del reparto; `buildActivation` es la de vuelta. Las
 * dos viven juntas para que no puedan divergir: una opción añadida aquí y
 * olvidada allá se marcaría al abrir la ficha y se perdería al guardar,
 * sin error y sin aviso.
 */
/**
 * La mitad de VUELTA: de las opciones marcadas al `status` que se guarda.
 *
 * `desde` es el estado que traía el producto al abrirlo, y es lo que hace
 * que el tercer valor no se pierda:
 *
 *  · marcada        → `published`, siempre.
 *  · sin marcar y venía ARCHIVADO   → sigue `archived`.
 *  · sin marcar y venía de cualquier otro → `draft`.
 *
 * Sin la segunda regla, abrir un producto retirado para corregirle una
 * coma y guardarlo lo sacaría del archivo y lo dejaría en borrador. No
 * fallaría nada: simplemente, el producto habría cambiado de estado sin
 * que nadie lo pidiera y sin que nada lo dijera.
 */
function activationToStatus(activation: readonly string[], desde: string): string {
  if (isActive(activation, ACTIVATION_OPTIONS.catalogActive)) return "published";
  return desde === "archived" ? "archived" : "draft";
}

function activationOf(product: Product): string[] {
  const options: string[] = [];
  if (product.status === "published") options.push(ACTIVATION_OPTIONS.catalogActive);
  // `?? true`: un producto guardado sin el campo se vendía; heredarlo
  // como "no disponible" apagaría el botón de comprar de medio catálogo
  // la primera vez que alguien abra y guarde una ficha antigua.
  if (product.isAvailable ?? true) options.push(ACTIVATION_OPTIONS.purchasable);
  if (product.compensation?.qualifiesForRank) options.push(ACTIVATION_OPTIONS.qualifying);
  if (product.shipping?.requiresShipping) options.push(ACTIVATION_OPTIONS.requiresShipping);
  return options;
}

// ── Los valores de partida ──────────────────────────────────────────

/**
 * Las monedas con las que nace un producto nuevo.
 *
 * Son un PUNTO DE PARTIDA, no la lista cerrada: el paso de precios deja
 * añadir y quitar. Están aquí porque son los dos mercados abiertos hoy y
 * teclearlas cada vez sería trabajo repetido, no porque el sistema sepa
 * solo de dos.
 */
export const DEFAULT_CURRENCIES = ["MXN", "USD"] as const;

const EMPTY_CONTENT: ProductContentFormValues = {
  name: "",
  tagline: "",
  description: "",
  disclaimer: "",
};

export const EMPTY_EDITORIAL: EditorialFormValues = {
  badgeLabel: "",
  compareAt: {},
  metadata: { content: "", dose: "", duration: "", cert: "" },
  pillars: { title: "", intro: "", items: [] },
  ingredients: { title: "", intro: "", theme: "", items: [] },
  specTable: { title: "", type: "", footer: "", items: [] },
  process: { title: "", intro: "", items: [] },
  stats: { title: "", intro: "", items: [] },
  sideBySide: {
    title: "",
    theme: "",
    left: { icon: "", title: "", description: "" },
    right: { icon: "", title: "", description: "" },
  },
  callout: { type: "", icon: "", title: "", body: "" },
  closing: { quote: "", theme: "" },
};

/** Una copia PROPIA del editorial vacío: los objetos anidados se comparten si no. */
function freshEditorial(): EditorialFormValues {
  return structuredClone(EMPTY_EDITORIAL);
}

export function emptyProductFormValues(): ProductFormValues {
  return {
    slug: "",
    translations: { es: { ...EMPTY_CONTENT }, en: { ...EMPTY_CONTENT } },
    category: "",
    type: "",
    sortOrder: "0",
    status: "draft",
    /*
     * Un producto nuevo nace COMPRABLE pero sin publicar: la ficha se
     * escribe entera antes de enseñarla, y llegar al final para
     * descubrir que además hay que marcar "se puede comprar" es un paso
     * de más para el caso normal.
     */
    activation: [ACTIVATION_OPTIONS.purchasable],
    items: [],
    benefits: [],
    extraCosts: [],
    currencies: DEFAULT_CURRENCIES.map((code) => ({ code })),
    prices: {},
    memberPrices: {},
    compareAtPrices: {},
    discount: { active: false, type: "percent", value: "" },
    volumePoints: "",
    compensation: {
      commissionPoints: "",
      promoVolumePoints: "",
      promoCommissionPoints: "",
      isQualifying: false,
      generatesPersonalPV: false,
      generatesGroupPV: false,
      qualifiesForText: "",
      isRepurchase: false,
      maintainsActive: false,
      membershipBenefitsText: "",
    },
    imageUrl: "",
    imageUrls: [],
    datasheet: { url: "", name: "" },
    facts: {
      sku: "",
      format: "",
      servingSize: "",
      servingsPerContainer: "",
      suggestedUse: "",
      manufacturedIn: "",
      manufacturedFor: "",
      certifications: [],
      ingredients: [],
      contraindications: [],
    },
    stock: "",
    shipping: {
      weightGrams: "",
      lengthCm: "",
      widthCm: "",
      heightCm: "",
    },
    validity: { from: "", to: "", durationDays: "" },
    isWholesale: false,
    franchiseeDiscountPercent: "",
    presentation: "",
    dosage: "",
    legend: "",
    editorial: { es: freshEditorial(), en: freshEditorial() },
  };
}

// ── De lo guardado a lo que se edita ────────────────────────────────

/** De centavos a lo que se escribe en el campo: "1299.50", sin símbolo. */
export function centsToInput(cents: number | undefined): string {
  if (cents === undefined) return "";
  return (cents / 100).toFixed(2);
}

function numberToInput(value: number | undefined): string {
  return value === undefined ? "" : String(value);
}

/** De un mapa de centavos a un mapa de lo que se teclea. */
function priceMapToInput(map: Record<string, number> | undefined): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [currency, cents] of Object.entries(map ?? {})) {
    out[currency] = centsToInput(cents);
  }
  return out;
}

/** De `"2026-08-26T00:00:00.000Z"` a `"2026-08-26"`, que es lo que pinta el campo. */
function isoToDateInput(value: string | undefined): string {
  if (!value) return "";
  return value.slice(0, 10);
}

function textsToItems(values: readonly string[] | undefined): TextItemValues[] {
  return (values ?? []).map((value) => ({ value }));
}

/**
 * Qué monedas enseña el paso de precios al abrir un producto guardado.
 *
 * La UNIÓN de las llaves de todos los mapas, y no solo las de `prices`:
 * un producto puede tener precio de socio en una moneda en la que
 * todavía no hay precio público, y si esa moneda no apareciera, su
 * precio de socio quedaría guardado e invisible — imposible de corregir
 * y a la vez vivo en la tienda.
 *
 * Si no hay ninguna, se cae a las de partida: un producto sin precios es
 * un formulario en el que hay que poder escribir uno.
 */
function currenciesOf(product: Product): CurrencyRowValues[] {
  const codes = new Set<string>();
  for (const map of [
    product.prices,
    product.memberPrices,
    /*
     * `promoPrices` sigue en la lista aunque ya no sea un campo: es un
     * mapa REAL del producto guardado, y una moneda que solo tuviera
     * precio promocional desaparecería del paso —y con ella la
     * posibilidad de darle un precio público.
     */
    product.promoPrices,
    product.compareAtPrices,
    product.editorial?.es.compareAt,
    product.editorial?.en?.compareAt,
  ]) {
    for (const code of Object.keys(map ?? {})) codes.add(code);
  }
  if (codes.size === 0) return DEFAULT_CURRENCIES.map((code) => ({ code }));
  return [...codes].sort().map((code) => ({ code }));
}

function editorialToFormValues(
  editorial: EditorialContent | undefined,
): EditorialFormValues {
  const base = freshEditorial();
  if (!editorial) return base;

  return {
    badgeLabel: editorial.badgeLabel ?? "",
    compareAt: priceMapToInput(editorial.compareAt),
    metadata: {
      content: editorial.metadata?.content ?? "",
      dose: editorial.metadata?.dose ?? "",
      duration: editorial.metadata?.duration ?? "",
      cert: editorial.metadata?.cert ?? "",
    },
    pillars: {
      title: editorial.pillars?.title ?? "",
      intro: editorial.pillars?.intro ?? "",
      items: (editorial.pillars?.items ?? []).map((item) => ({
        icon: item.icon ?? "",
        title: item.title,
        description: item.description ?? "",
      })),
    },
    ingredients: {
      title: editorial.ingredients?.title ?? "",
      intro: editorial.ingredients?.intro ?? "",
      theme: editorial.ingredients?.theme ?? "",
      items: (editorial.ingredients?.items ?? []).map((item) => ({
        icon: item.icon ?? "",
        name: item.name,
        spec: item.spec ?? "",
        description: item.description ?? "",
      })),
    },
    specTable: {
      title: editorial.specTable?.title ?? "",
      type: editorial.specTable?.type ?? "",
      footer: editorial.specTable?.footer ?? "",
      items: (editorial.specTable?.items ?? []).map((item) => ({
        name: item.name,
        dose: item.dose ?? "",
        unit: item.unit ?? "",
        function: item.function ?? "",
      })),
    },
    process: {
      title: editorial.process?.title ?? "",
      intro: editorial.process?.intro ?? "",
      items: (editorial.process?.items ?? []).map((item) => ({
        icon: item.icon ?? "",
        title: item.title,
        description: item.description ?? "",
      })),
    },
    stats: {
      title: editorial.stats?.title ?? "",
      intro: editorial.stats?.intro ?? "",
      items: (editorial.stats?.items ?? []).map((item) => ({
        number: item.number,
        label: item.label,
        description: item.description ?? "",
      })),
    },
    sideBySide: {
      title: editorial.sideBySide?.title ?? "",
      theme: editorial.sideBySide?.theme ?? "",
      left: {
        icon: editorial.sideBySide?.left.icon ?? "",
        title: editorial.sideBySide?.left.title ?? "",
        description: editorial.sideBySide?.left.description ?? "",
      },
      right: {
        icon: editorial.sideBySide?.right.icon ?? "",
        title: editorial.sideBySide?.right.title ?? "",
        description: editorial.sideBySide?.right.description ?? "",
      },
    },
    callout: {
      type: editorial.callout?.type ?? "",
      icon: editorial.callout?.icon ?? "",
      title: editorial.callout?.title ?? "",
      body: editorial.callout?.body ?? "",
    },
    closing: {
      quote: editorial.closing?.quote ?? "",
      theme: editorial.closing?.theme ?? "",
    },
  };
}

/** Un producto del API, listo para editarse. */
export function productToFormValues(product: Product): ProductFormValues {
  const en = product.translations.en;
  const compensation = product.compensation;
  const shipping = product.shipping;

  return {
    slug: product.slug,
    translations: {
      es: {
        name: product.translations.es.name,
        tagline: product.translations.es.tagline ?? "",
        description: product.translations.es.description ?? "",
        disclaimer: product.translations.es.disclaimer ?? "",
      },
      en: {
        name: en?.name ?? "",
        tagline: en?.tagline ?? "",
        description: en?.description ?? "",
        disclaimer: en?.disclaimer ?? "",
      },
    },
    category: product.category ?? "",
    type: product.type ?? "",
    sortOrder: String(product.sortOrder),
    /*
     * El estado se conserva TAL CUAL —incluido `archived`— aunque no
     * tenga control propio: es lo que permite que guardar un producto
     * retirado no lo resucite. Ver `ACTIVATION_OPTIONS`.
     */
    status: product.status,
    activation: activationOf(product),
    items: (product.items ?? []).map((item) => ({
      productId: item.productId,
      quantity: String(item.quantity),
    })),
    benefits: textsToItems(product.benefits),
    extraCosts: (product.extraCosts ?? []).map((cost) => ({
      value: cost.value,
      amounts: priceMapToInput(cost.amounts),
    })),

    currencies: currenciesOf(product),
    prices: priceMapToInput(product.prices),
    memberPrices: priceMapToInput(product.memberPrices),
    compareAtPrices: priceMapToInput(product.compareAtPrices),
    discount: {
      active: product.discount?.active ?? false,
      type: product.discount?.type ?? "percent",
      /*
       * Un descuento por PORCENTAJE se guarda como número llano (15), y
       * uno FIJO en centavos como cualquier otro importe. Por eso el
       * mismo campo se lee de dos maneras: mostrar "1500" donde se
       * guardaron 1500 centavos sería ofrecer un descuento cien veces
       * mayor al que alguien escribió.
       */
      value:
        product.discount === undefined
          ? ""
          : product.discount.type === "fixed"
            ? centsToInput(product.discount.value)
            : String(product.discount.value),
    },

    volumePoints: numberToInput(product.volumePoints),
    compensation: {
      commissionPoints: numberToInput(compensation?.commissionPoints),
      promoVolumePoints: numberToInput(compensation?.promoVolumePoints),
      promoCommissionPoints: numberToInput(compensation?.promoCommissionPoints),
      isQualifying: compensation?.isQualifying ?? false,
      generatesPersonalPV: compensation?.generatesPersonalPV ?? false,
      generatesGroupPV: compensation?.generatesGroupPV ?? false,
      qualifiesForText: compensation?.qualifiesForText ?? "",
      isRepurchase: compensation?.isRepurchase ?? false,
      maintainsActive: compensation?.maintainsActive ?? false,
      membershipBenefitsText: compensation?.membershipBenefitsText ?? "",
    },

    imageUrl: product.imageUrl ?? "",
    imageUrls: textsToItems(product.imageUrls),
    datasheet: {
      url: product.datasheet?.url ?? "",
      name: product.datasheet?.name ?? "",
    },
    facts: {
      sku: product.facts?.sku ?? "",
      format: product.facts?.format ?? "",
      servingSize: product.facts?.servingSize ?? "",
      servingsPerContainer: numberToInput(product.facts?.servingsPerContainer),
      suggestedUse: product.facts?.suggestedUse ?? "",
      manufacturedIn: product.facts?.manufacturedIn ?? "",
      manufacturedFor: product.facts?.manufacturedFor ?? "",
      certifications: textsToItems(product.facts?.certifications),
      ingredients: textsToItems(product.facts?.ingredients),
      contraindications: textsToItems(product.facts?.contraindications),
    },

    stock: numberToInput(product.stock),
    shipping: {
      weightGrams: numberToInput(shipping?.weightGrams),
      lengthCm: numberToInput(shipping?.lengthCm),
      widthCm: numberToInput(shipping?.widthCm),
      heightCm: numberToInput(shipping?.heightCm),
    },

    validity: {
      from: isoToDateInput(product.validity?.from),
      to: isoToDateInput(product.validity?.to),
      durationDays: numberToInput(product.validity?.durationDays),
    },

    isWholesale: product.isWholesale ?? false,
    franchiseeDiscountPercent: numberToInput(product.franchiseeDiscountPercent),
    presentation: product.presentation ?? "",
    dosage: product.dosage ?? "",
    legend: product.legend ?? "",

    editorial: {
      es: editorialToFormValues(product.editorial?.es),
      en: editorialToFormValues(product.editorial?.en),
    },
  };
}

// ── Leer lo que se escribió ─────────────────────────────────────────

/** `undefined` si el campo está vacío; el texto limpio si no. */
function textOrUndefined(raw: string): string | undefined {
  const trimmed = raw.trim();
  return trimmed === "" ? undefined : trimmed;
}

/** `undefined` si está vacío; los centavos si se entiende; `null` si no. */
function readMoney(raw: string): number | null | undefined {
  const trimmed = raw.trim();
  if (trimmed === "") return undefined;
  return parsePriceToCents(trimmed);
}

function readInteger(raw: string): number | null | undefined {
  const trimmed = raw.trim();
  if (trimmed === "") return undefined;
  if (!/^-?\d+$/.test(trimmed)) return null;
  return Number(trimmed);
}

function readDecimal(raw: string): number | null | undefined {
  const trimmed = raw.trim();
  if (trimmed === "") return undefined;
  if (!/^-?\d*\.?\d+$/.test(trimmed)) return null;
  return Number(trimmed);
}

/** Una fecha del campo (`YYYY-MM-DD`) como instante ISO, o `null` si no cuadra. */
function readDate(raw: string): string | null | undefined {
  const trimmed = raw.trim();
  if (trimmed === "") return undefined;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return null;
  const parsed = new Date(`${trimmed}T00:00:00.000Z`);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toISOString();
}

/** Las filas con texto, ya limpias. Las vacías simplemente no cuentan. */
function readTextItems(items: readonly TextItemValues[]): string[] {
  return items.map((item) => item.value.trim()).filter((value) => value !== "");
}

/**
 * El mapa de precios, en centavos.
 *
 * Una moneda sin valor NO viaja como `undefined`: se OMITE. Mandar
 * `{ MXN: undefined }` en un `Record<string, number>` es un tipo
 * mentiroso, y al serializar deja una llave fantasma.
 *
 * `allowed` es la lista de monedas ACTIVAS del producto, y filtrar por
 * ella no es una precaución teórica: quitar el euro del paso de precios
 * borra su fila, pero el importe que se escribió sigue en el formulario
 * bajo `prices.EUR`. Sin este filtro, una moneda quitada seguiría
 * viajando en cada envío —invisible en pantalla y viva en la tienda— y
 * no habría forma de quitarla.
 */
function buildPrices(
  raw: Record<string, string>,
  allowed: ReadonlySet<string>,
): Record<string, number> {
  const out: Record<string, number> = {};
  for (const [currency, value] of Object.entries(raw)) {
    if (!allowed.has(currency)) continue;
    const cents = readMoney(value);
    // `null` = escrito pero ilegible · `undefined` = vacío. Ninguno de
    // los dos entra al mapa: uno lo rechaza el esquema aparte, y el otro
    // simplemente no es un precio.
    if (typeof cents === "number") out[currency] = cents;
  }
  return out;
}

/** Las monedas activas del producto, limpias y en mayúsculas. */
function activeCurrencies(rows: readonly CurrencyRowValues[]): Set<string> {
  return new Set(
    rows.map((row) => row.code.trim().toUpperCase()).filter((code) => code !== ""),
  );
}

// ── El esquema de lo que se escribe ─────────────────────────────────

const textItemSchema = z.object({ value: z.string() });

const contentValuesSchema = z.object({
  name: z.string(),
  tagline: z.string(),
  description: z.string(),
  disclaimer: z.string(),
});

const pillarValuesSchema = z.object({
  icon: z.string(),
  title: z.string(),
  description: z.string(),
});

const editorialValuesSchema = z.object({
  badgeLabel: z.string(),
  compareAt: z.record(z.string(), z.string()),
  metadata: z.object({
    content: z.string(),
    dose: z.string(),
    duration: z.string(),
    cert: z.string(),
  }),
  pillars: z.object({
    title: z.string(),
    intro: z.string(),
    items: z.array(pillarValuesSchema),
  }),
  ingredients: z.object({
    title: z.string(),
    intro: z.string(),
    theme: z.string(),
    items: z.array(
      z.object({
        icon: z.string(),
        name: z.string(),
        spec: z.string(),
        description: z.string(),
      }),
    ),
  }),
  specTable: z.object({
    title: z.string(),
    type: z.string(),
    footer: z.string(),
    items: z.array(
      z.object({
        name: z.string(),
        dose: z.string(),
        unit: z.string(),
        function: z.string(),
      }),
    ),
  }),
  process: z.object({
    title: z.string(),
    intro: z.string(),
    items: z.array(pillarValuesSchema),
  }),
  stats: z.object({
    title: z.string(),
    intro: z.string(),
    items: z.array(
      z.object({ number: z.string(), label: z.string(), description: z.string() }),
    ),
  }),
  sideBySide: z.object({
    title: z.string(),
    theme: z.string(),
    left: pillarValuesSchema,
    right: pillarValuesSchema,
  }),
  callout: z.object({
    type: z.string(),
    icon: z.string(),
    title: z.string(),
    body: z.string(),
  }),
  closing: z.object({ quote: z.string(), theme: z.string() }),
});

const formValuesSchema = z.object({
  slug: z.string(),
  translations: z.object({ es: contentValuesSchema, en: contentValuesSchema }),
  category: z.string(),
  type: z.string(),
  sortOrder: z.string(),
  status: z.string(),
  activation: z.array(z.string()),
  items: z.array(z.object({ productId: z.string(), quantity: z.string() })),
  benefits: z.array(textItemSchema),
  extraCosts: z.array(
    z.object({ value: z.string(), amounts: z.record(z.string(), z.string()) }),
  ),
  currencies: z.array(z.object({ code: z.string() })),
  prices: z.record(z.string(), z.string()),
  memberPrices: z.record(z.string(), z.string()),
  compareAtPrices: z.record(z.string(), z.string()),
  discount: z.object({
    active: z.boolean(),
    type: z.string(),
    value: z.string(),
  }),
  volumePoints: z.string(),
  compensation: z.object({
    commissionPoints: z.string(),
    promoVolumePoints: z.string(),
    promoCommissionPoints: z.string(),
    isQualifying: z.boolean(),
    generatesPersonalPV: z.boolean(),
    generatesGroupPV: z.boolean(),
    qualifiesForText: z.string(),
    isRepurchase: z.boolean(),
    maintainsActive: z.boolean(),
    membershipBenefitsText: z.string(),
  }),
  imageUrl: z.string(),
  imageUrls: z.array(textItemSchema),
  datasheet: z.object({ url: z.string(), name: z.string() }),
  facts: z.object({
    sku: z.string(),
    format: z.string(),
    servingSize: z.string(),
    servingsPerContainer: z.string(),
    suggestedUse: z.string(),
    manufacturedIn: z.string(),
    manufacturedFor: z.string(),
    certifications: z.array(textItemSchema),
    ingredients: z.array(textItemSchema),
    contraindications: z.array(textItemSchema),
  }),
  stock: z.string(),
  shipping: z.object({
    weightGrams: z.string(),
    lengthCm: z.string(),
    widthCm: z.string(),
    heightCm: z.string(),
  }),
  validity: z.object({ from: z.string(), to: z.string(), durationDays: z.string() }),
  isWholesale: z.boolean(),
  franchiseeDiscountPercent: z.string(),
  presentation: z.string(),
  dosage: z.string(),
  legend: z.string(),
  editorial: z.object({ es: editorialValuesSchema, en: editorialValuesSchema }),
});

/** Cada aviso, con su ruta, exactamente donde se escribió. */
type Ctx = z.core.$RefinementCtx<z.infer<typeof formValuesSchema>>;

function issue(ctx: Ctx, path: (string | number)[], message: string): void {
  ctx.addIssue({ code: "custom", path, message });
}

const MONEY_MESSAGE = "Escribe un monto válido, por ejemplo 1299.50";
const INTEGER_MESSAGE = "Escribe un número entero";

/** Revisa un campo de dinero y avisa en su sitio si no se entiende. */
function checkMoney(ctx: Ctx, raw: string, path: (string | number)[]): void {
  if (readMoney(raw) === null) issue(ctx, path, MONEY_MESSAGE);
}

function checkInteger(
  ctx: Ctx,
  raw: string,
  path: (string | number)[],
  message = INTEGER_MESSAGE,
): void {
  const value = readInteger(raw);
  if (value === null || (typeof value === "number" && value < 0)) {
    issue(ctx, path, message);
  }
}

function checkDecimal(ctx: Ctx, raw: string, path: (string | number)[]): void {
  const value = readDecimal(raw);
  if (value === null || (typeof value === "number" && value < 0)) {
    issue(ctx, path, "Escribe un número, 0 o más");
  }
}

// ── Del formulario al contrato: los bloques editoriales ─────────────
//
// TODO bloque editorial es opcional, y esa es la regla que hace posible
// guardar un borrador: un producto sin editorial es válido y su ficha
// enseña lo que tenga. Así que cada bloque se construye con la misma
// pregunta —¿hay ALGO escrito aquí?— y si la respuesta es no, el bloque
// no viaja. Media ficha es mejor que una plantilla con huecos.
//
// El caso que sí se avisa es el intermedio: filas escritas y el título
// del bloque vacío. Ahí el contrato rechazaría el bloque entero con un
// mensaje sobre `title`, y sin este aviso previo el error saldría en un
// campo que quien escribe ni siquiera vio.

function anyText(...values: (string | undefined)[]): boolean {
  return values.some((value) => (value ?? "").trim() !== "");
}

type EditorialInput = NonNullable<z.input<typeof productInputSchema>["editorial"]>["es"];

/*
 * Los `as` de `buildEditorial`, explicados una vez.
 *
 * Un `<select>` produce TEXTO, y TypeScript no puede saber que ese texto
 * es uno de los doce iconos permitidos. La afirmación es segura porque
 * lo siguiente que ocurre es `.pipe(productInputSchema)`: si alguien
 * manipula el desplegable y manda "unicornio", el contrato lo rechaza
 * con el error en su campo. Afirmar aquí y validar allí es lo contrario
 * de tapar el problema: es dejar que lo resuelva quien manda.
 */
type EditorialIconInput = EditorialIcon | undefined;
type EditorialThemeInput = EditorialTheme | undefined;
type CalloutTypeInput = EditorialCalloutType | undefined;
type ClosingThemeInput = z.infer<typeof editorialClosingThemeSchema> | undefined;

function buildEditorial(
  values: EditorialFormValues,
  currencies: ReadonlySet<string>,
): EditorialInput {
  const pillarItems = values.pillars.items
    .filter((item) => anyText(item.title, item.description))
    .map((item) => ({
      icon: textOrUndefined(item.icon) as EditorialIconInput,
      title: item.title.trim(),
      description: textOrUndefined(item.description),
    }));

  const processItems = values.process.items
    .filter((item) => anyText(item.title, item.description))
    .map((item) => ({
      icon: textOrUndefined(item.icon) as EditorialIconInput,
      title: item.title.trim(),
      description: textOrUndefined(item.description),
    }));

  const ingredientItems = values.ingredients.items
    .filter((item) => anyText(item.name, item.spec, item.description))
    .map((item) => ({
      icon: textOrUndefined(item.icon) as EditorialIconInput,
      name: item.name.trim(),
      spec: textOrUndefined(item.spec),
      description: textOrUndefined(item.description),
    }));

  const specRows = values.specTable.items
    .filter((item) => anyText(item.name, item.dose, item.unit, item.function))
    .map((item) => ({
      name: item.name.trim(),
      dose: textOrUndefined(item.dose),
      unit: textOrUndefined(item.unit),
      function: textOrUndefined(item.function),
    }));

  const statItems = values.stats.items
    .filter((item) => anyText(item.number, item.label, item.description))
    .map((item) => ({
      number: item.number.trim(),
      label: item.label.trim(),
      description: textOrUndefined(item.description),
    }));

  const compareAt = buildPrices(values.compareAt, currencies);

  const metadata = {
    content: textOrUndefined(values.metadata.content),
    dose: textOrUndefined(values.metadata.dose),
    duration: textOrUndefined(values.metadata.duration),
    cert: textOrUndefined(values.metadata.cert),
  };
  const hasMetadata = Object.values(metadata).some((value) => value !== undefined);

  const sideBySide = values.sideBySide;
  const hasSideBySide = anyText(
    sideBySide.title,
    sideBySide.left.title,
    sideBySide.left.description,
    sideBySide.right.title,
    sideBySide.right.description,
  );

  return {
    badgeLabel: textOrUndefined(values.badgeLabel),
    compareAt: Object.keys(compareAt).length > 0 ? compareAt : undefined,
    metadata: hasMetadata ? metadata : undefined,
    pillars:
      pillarItems.length > 0
        ? {
            title: values.pillars.title.trim(),
            intro: textOrUndefined(values.pillars.intro),
            items: pillarItems,
          }
        : undefined,
    ingredients:
      ingredientItems.length > 0
        ? {
            title: values.ingredients.title.trim(),
            intro: textOrUndefined(values.ingredients.intro),
            theme: textOrUndefined(values.ingredients.theme) as EditorialThemeInput,
            items: ingredientItems,
          }
        : undefined,
    specTable:
      specRows.length > 0
        ? {
            title: values.specTable.title.trim(),
            type: textOrUndefined(values.specTable.type),
            footer: textOrUndefined(values.specTable.footer),
            items: specRows,
          }
        : undefined,
    process:
      processItems.length > 0
        ? {
            title: values.process.title.trim(),
            intro: textOrUndefined(values.process.intro),
            items: processItems,
          }
        : undefined,
    stats:
      statItems.length > 0
        ? {
            title: values.stats.title.trim(),
            intro: textOrUndefined(values.stats.intro),
            items: statItems,
          }
        : undefined,
    sideBySide: hasSideBySide
      ? {
          title: sideBySide.title.trim(),
          theme: textOrUndefined(sideBySide.theme) as EditorialThemeInput,
          left: {
            icon: textOrUndefined(sideBySide.left.icon) as EditorialIconInput,
            title: sideBySide.left.title.trim(),
            description: textOrUndefined(sideBySide.left.description),
          },
          right: {
            icon: textOrUndefined(sideBySide.right.icon) as EditorialIconInput,
            title: sideBySide.right.title.trim(),
            description: textOrUndefined(sideBySide.right.description),
          },
        }
      : undefined,
    callout: anyText(values.callout.title, values.callout.body)
      ? {
          type: textOrUndefined(values.callout.type) as CalloutTypeInput,
          icon: textOrUndefined(values.callout.icon) as EditorialIconInput,
          title: values.callout.title.trim(),
          body: textOrUndefined(values.callout.body),
        }
      : undefined,
    closing: anyText(values.closing.quote)
      ? {
          quote: values.closing.quote.trim(),
          theme: textOrUndefined(values.closing.theme) as ClosingThemeInput,
        }
      : undefined,
  };
}

/** ¿Hay algo escrito en este editorial? Decide si el bloque entero viaja. */
function editorialHasContent(built: EditorialInput): boolean {
  return Object.values(built).some((value) => value !== undefined);
}

// ── El esquema completo ─────────────────────────────────────────────

/**
 * Revisa lo que se escribió, lo traduce a la forma del contrato y deja
 * que el CONTRATO tenga la última palabra.
 *
 * Las tres etapas importan en ese orden. Lo que solo el formulario sabe
 * —"eso no parece un monto", "el precio tachado tiene que ser mayor",
 * "esa moneda no es un código ISO"— se comprueba antes de convertir, con
 * la ruta del campo que lo dice. Lo que define el negocio —cuánto mide
 * un nombre, qué forma tiene un slug, qué iconos existen— no se
 * reimplementa aquí: se delega en `productInputSchema`, que es la única
 * fuente.
 */
export const productFormSchema = formValuesSchema
  .superRefine((values, ctx) => {
    // ── Monedas ────────────────────────────────────────────────────
    const seen = new Set<string>();
    values.currencies.forEach((row, index) => {
      const code = row.code.trim().toUpperCase();
      if (!/^[A-Z]{3}$/.test(code)) {
        issue(ctx, ["currencies", index, "code"], "Usa un código de tres letras: MXN");
        return;
      }
      if (seen.has(code)) {
        issue(ctx, ["currencies", index, "code"], "Esa moneda ya está en la lista");
      }
      seen.add(code);
    });

    // ── Precios ────────────────────────────────────────────────────
    for (const code of seen) {
      checkMoney(ctx, values.prices[code] ?? "", ["prices", code]);
      checkMoney(ctx, values.memberPrices[code] ?? "", ["memberPrices", code]);

      /*
       * El precio de SOCIO no puede ser mayor que el público: una
       * membresía que sale más cara que no tenerla no es una membresía.
       * Se atrapa aquí y no en el contrato por lo mismo que el precio
       * tachado: el seed y los scripts crean productos por otra puerta.
       */
      const publico = readMoney(values.prices[code] ?? "");
      const socio = readMoney(values.memberPrices[code] ?? "");
      if (
        typeof publico === "number" &&
        typeof socio === "number" &&
        socio > publico
      ) {
        issue(
          ctx,
          ["memberPrices", code],
          "El precio de socio no puede ser mayor que el público",
        );
      }

      const price = readMoney(values.prices[code] ?? "");
      const compareAt = readMoney(values.compareAtPrices[code] ?? "");
      if (compareAt === null) {
        checkMoney(ctx, values.compareAtPrices[code] ?? "", ["compareAtPrices", code]);
        continue;
      }

      /*
       * El precio tachado es la promesa de una rebaja. Si no es mayor
       * que el precio real, la rebaja es mentira: se enseñaría "antes
       * $200, ahora $300". Se atrapa aquí porque el contrato solo lo
       * documenta.
       */
      if (compareAt !== undefined && typeof price === "number" && compareAt <= price) {
        issue(
          ctx,
          ["compareAtPrices", code],
          "El precio tachado debe ser mayor que el precio",
        );
      }
    }

    /*
     * El contrato exige al menos un precio. El aviso va en el campo de la
     * PRIMERA moneda de la lista —la primera que se ve— y no en un error
     * suelto sin dueño que obliga a adivinar dónde escribir.
     */
    const anyPrice = [...seen].some(
      (code) => typeof readMoney(values.prices[code] ?? "") === "number",
    );
    if (!anyPrice) {
      const first = values.currencies[0]?.code.trim().toUpperCase() ?? "MXN";
      issue(ctx, ["prices", first], "Pon al menos un precio");
    }

    // ── Clasificación ──────────────────────────────────────────────
    /*
     * La categoría es OBLIGATORIA (Anahí, 26-ago), y se exige AQUÍ y no
     * en el contrato compartido.
     *
     * La diferencia importa: hacerla obligatoria en `productInputSchema`
     * invalidaría de golpe todo lo que crea productos por otra puerta
     * —el seed, un script de importación, las pruebas del API que no
     * hablan de categorías— y obligaría a una migración solo para
     * seguir sembrando el catálogo de siempre.
     *
     * Exigirla en el FORMULARIO consigue lo que se pedía: nadie puede
     * dar de alta un producto sin categoría desde el panel, que es donde
     * se dan de alta. El servidor sigue aceptando lo que ya aceptaba.
     */
    if (values.category.trim() === "") {
      issue(ctx, ["category"], "Elige una categoría");
    }

    // ── Composición del paquete ────────────────────────────────────
    /*
     * Solo se revisa cuando el tipo se comporta como paquete: en
     * cualquier otro caso las líneas ni se pintan ni se envían, y sacar
     * en rojo un renglón invisible es un "Guardar" que no hace nada.
     *
     * El DUPLICADO se atrapa también aquí, además de en el servidor. No
     * es redundancia por gusto: el selector ya deshabilita lo elegido,
     * así que llegar hasta el servidor para descubrirlo significaría que
     * algo raro pasó — y aun así el aviso tiene que aterrizar en el
     * renglón que sobra, no en un cartel arriba del formulario.
     */
    if (behaviorOfType(values.type.trim() || undefined) === "package") {
      const vistos = new Map<string, number>();
      values.items.forEach((item, index) => {
        const id = item.productId.trim();
        if (id === "") {
          issue(ctx, ["items", index, "productId"], "Elige un producto");
          return;
        }
        const antes = vistos.get(id);
        if (antes !== undefined) {
          issue(
            ctx,
            ["items", index, "productId"],
            `Ese producto ya está en la línea ${antes + 1}`,
          );
          return;
        }
        vistos.set(id, index);
        checkInteger(
          ctx,
          item.quantity,
          ["items", index, "quantity"],
          "Las unidades son un número entero, 1 o más",
        );
        const cantidad = readInteger(item.quantity);
        if (typeof cantidad === "number" && (cantidad < 1 || cantidad > 99)) {
          issue(ctx, ["items", index, "quantity"], "De 1 a 99 unidades");
        }
      });
    }

    // ── Beneficios ─────────────────────────────────────────────────
    /*
     * El DUPLICADO se atrapa aquí, además de en el servidor. El selector
     * ya deshabilita lo elegido, así que llegar hasta el servidor
     * significaría que algo raro pasó — y aun así el aviso tiene que
     * aterrizar en el renglón que sobra, no en un cartel arriba.
     */
    const beneficiosVistos = new Map<string, number>();
    values.benefits.forEach((row, index) => {
      const value = row.value.trim();
      if (value === "") {
        issue(ctx, ["benefits", index, "value"], "Elige un beneficio");
        return;
      }
      const antes = beneficiosVistos.get(value);
      if (antes !== undefined) {
        issue(
          ctx,
          ["benefits", index, "value"],
          `Ese beneficio ya está en la línea ${antes + 1}`,
        );
        return;
      }
      beneficiosVistos.set(value, index);
    });

    // ── Variantes y costos adicionales ─────────────────────────────
    const costosVistos = new Map<string, number>();
    values.extraCosts.forEach((row, index) => {
      const value = row.value.trim();
      if (value === "") {
        issue(ctx, ["extraCosts", index, "value"], "Elige de qué es el costo");
        return;
      }
      const antes = costosVistos.get(value);
      if (antes !== undefined) {
        issue(
          ctx,
          ["extraCosts", index, "value"],
          `Ese costo ya está en la línea ${antes + 1}`,
        );
        return;
      }
      costosVistos.set(value, index);

      // El monto se revisa moneda a moneda, y solo el de las ACTIVAS: el
      // importe de una moneda quitada del paso 3 no se pinta en ningún
      // sitio, así que un aviso sobre él no tendría dónde salir.
      for (const code of seen) {
        checkMoney(ctx, row.amounts[code] ?? "", ["extraCosts", index, "amounts", code]);
      }
    });

    // ── La dirección pública, que ya no se teclea ──────────────────
    /*
     * El slug se genera del nombre en español, así que un nombre que no
     * deja nada utilizable —"¿!?"— produciría un slug que el contrato
     * rechaza… en un campo que ya no existe, o sea un "Crear producto"
     * que no hace nada y que no se puede desatascar.
     *
     * El aviso va en el NOMBRE, que es el campo que quien escribe sí
     * tiene delante y sí puede cambiar.
     */
    /*
     * Solo cuando ya hay un nombre ESCRITO. Con el campo vacío, quien
     * manda es el mínimo del contrato ("Escribe el nombre"), y sacar dos
     * avisos sobre el mismo hueco es un aviso de más.
     */
    const nombreEs = values.translations.es.name.trim();
    if (
      values.slug.trim() === "" &&
      nombreEs !== "" &&
      suggestSlug(nombreEs).length < 3
    ) {
      issue(
        ctx,
        ["translations", "es", "name"],
        "Ese nombre no genera una dirección para la tienda: añade alguna letra o número",
      );
    }

    // ── Descuento ──────────────────────────────────────────────────
    const discountValue = values.discount.value.trim();
    if (values.discount.active && discountValue === "") {
      issue(ctx, ["discount", "value"], "Escribe cuánto se descuenta");
    }
    if (discountValue !== "") {
      if (values.discount.type === "percent") {
        const percent = readDecimal(discountValue);
        if (percent === null || percent === undefined || percent < 0 || percent > 100) {
          issue(ctx, ["discount", "value"], "El porcentaje va de 0 a 100");
        }
      } else {
        checkMoney(ctx, discountValue, ["discount", "value"]);
      }
    }

    // ── Números sueltos ────────────────────────────────────────────
    checkInteger(ctx, values.sortOrder, ["sortOrder"], "El orden es un número entero");
    checkInteger(
      ctx,
      values.stock,
      ["stock"],
      "Las existencias son un número entero, 0 o más",
    );
    checkInteger(ctx, values.volumePoints, ["volumePoints"]);
    checkInteger(ctx, values.compensation.commissionPoints, [
      "compensation",
      "commissionPoints",
    ]);
    checkInteger(ctx, values.compensation.promoVolumePoints, [
      "compensation",
      "promoVolumePoints",
    ]);
    checkInteger(ctx, values.compensation.promoCommissionPoints, [
      "compensation",
      "promoCommissionPoints",
    ]);
    checkInteger(ctx, values.shipping.weightGrams, ["shipping", "weightGrams"]);
    checkDecimal(ctx, values.shipping.lengthCm, ["shipping", "lengthCm"]);
    checkDecimal(ctx, values.shipping.widthCm, ["shipping", "widthCm"]);
    checkDecimal(ctx, values.shipping.heightCm, ["shipping", "heightCm"]);
    checkInteger(ctx, values.validity.durationDays, ["validity", "durationDays"]);

    const franchisee = readDecimal(values.franchiseeDiscountPercent);
    if (
      franchisee === null ||
      (typeof franchisee === "number" && (franchisee < 0 || franchisee > 50))
    ) {
      issue(ctx, ["franchiseeDiscountPercent"], "El porcentaje va de 0 a 50");
    }

    // ── Vigencia ───────────────────────────────────────────────────
    const from = readDate(values.validity.from);
    const to = readDate(values.validity.to);
    if (from === null) issue(ctx, ["validity", "from"], "Esa fecha no es válida");
    if (to === null) issue(ctx, ["validity", "to"], "Esa fecha no es válida");
    if (typeof from === "string" && typeof to === "string" && to < from) {
      issue(ctx, ["validity", "to"], "El fin no puede ir antes del inicio");
    }

    // ── Editorial: filas escritas sin título de bloque ─────────────
    for (const locale of ["es", "en"] as const) {
      const editorial = values.editorial[locale];
      const blocks = [
        { key: "pillars", filled: editorial.pillars.items.some((i) => anyText(i.title)) },
        {
          key: "ingredients",
          filled: editorial.ingredients.items.some((i) => anyText(i.name)),
        },
        {
          key: "specTable",
          filled: editorial.specTable.items.some((i) => anyText(i.name)),
        },
        { key: "process", filled: editorial.process.items.some((i) => anyText(i.title)) },
        { key: "stats", filled: editorial.stats.items.some((i) => anyText(i.number)) },
      ] as const;

      for (const block of blocks) {
        if (block.filled && !anyText(editorial[block.key].title)) {
          issue(
            ctx,
            ["editorial", locale, block.key, "title"],
            "Ponle título al bloque: sin él no se puede guardar",
          );
        }
      }

      for (const code of Object.keys(editorial.compareAt)) {
        checkMoney(ctx, editorial.compareAt[code] ?? "", [
          "editorial",
          locale,
          "compareAt",
          code,
        ]);
      }
    }
  })
  .transform((values): z.input<typeof productInputSchema> => {
    const es = values.translations.es;
    const en = values.translations.en;

    /*
     * El contenido inglés YA NO lleva beneficios: los escribe el
     * servidor resolviendo el catálogo. Mandarlos desde aquí sería la
     * segunda fuente que este cambio viene a quitar.
     */
    const englishContent = {
      name: textOrUndefined(en.name),
      tagline: textOrUndefined(en.tagline),
      description: textOrUndefined(en.description),
      disclaimer: textOrUndefined(en.disclaimer),
    };
    const hasEnglish = Object.values(englishContent).some(
      (value) => value !== undefined,
    );

    const images = readTextItems(values.imageUrls);

    const discountValue =
      values.discount.type === "fixed"
        ? readMoney(values.discount.value)
        : readDecimal(values.discount.value);

    const currencies = activeCurrencies(values.currencies);

    const editorialEs = buildEditorial(values.editorial.es, currencies);
    const editorialEn = buildEditorial(values.editorial.en, currencies);

    const compareAtPrices = buildPrices(values.compareAtPrices, currencies);
    const memberPrices = buildPrices(values.memberPrices, currencies);

    return {
      /*
       * La dirección pública se DERIVA del nombre en español (Anahí,
       * 26-ago: "en ningún momento te pedí este campo"). Al EDITAR,
       * `values.slug` trae el que ya tiene el producto y se conserva —da
       * igual, porque `toProductUpdate` lo quita: un slug que cambia
       * rompe cada enlace ya compartido—. Al CREAR viene vacío y sale de
       * aquí.
       *
       * Si el nombre produce un slug ya ocupado, lo resuelve el SERVIDOR
       * poniéndole un sufijo. No puede devolver un 409: ya no hay campo
       * donde corregirlo.
       */
      slug: values.slug.trim() || suggestSlug(es.name),
      translations: {
        es: {
          name: es.name.trim(),
          tagline: textOrUndefined(es.tagline),
          description: textOrUndefined(es.description),
          disclaimer: textOrUndefined(es.disclaimer),
        },
        en: hasEnglish ? englishContent : undefined,
      },

      category: textOrUndefined(values.category),
      type: textOrUndefined(values.type) as ProductInput["type"],
      /*
       * El estado sale del reparto de `activation`, no de un desplegable.
       *
       * `activationToStatus` es lo que conserva el tercer valor: un
       * producto ARCHIVADO al que se le desmarca "Activo en catálogo"
       * sigue archivado, en vez de convertirse en un borrador y salir
       * del archivo sin que nadie lo pidiera.
       *
       * La afirmación de tipo es segura porque lo siguiente que pasa es
       * `.pipe(productInputSchema)`: cualquier estado imposible lo
       * rechaza el contrato con un error en el campo `status`.
       */
      status: activationToStatus(values.activation, values.status) as ProductStatus,
      sortOrder: readInteger(values.sortOrder) ?? 0,
      isAvailable: isActive(values.activation, ACTIVATION_OPTIONS.purchasable),

      /*
       * La composición viaja SOLO cuando el tipo se comporta como
       * paquete, y viaja SIEMPRE que lo hace —vacía incluida— porque una
       * edición es un PATCH: si desapareciera al quitar la última línea,
       * no habría forma de vaciar un paquete desde el formulario.
       *
       * `behaviorOfType` sin la lista cargada cae a la tabla de tipos de
       * sistema, que cubre los nueve que el código conoce; un tipo
       * inventado desde el panel cae a "producto", que es el más
       * inofensivo. El servidor vuelve a decidir lo mismo con la lista
       * real delante, así que aquí basta con no mandar de más.
       */
      items:
        behaviorOfType(textOrUndefined(values.type)) === "package"
          ? values.items
              .filter((item) => item.productId.trim() !== "")
              .map((item) => ({
                productId: item.productId.trim(),
                quantity: readInteger(item.quantity) ?? 1,
              }))
          : undefined,

      /*
       * Los BENEFICIOS viajan como REFERENCIAS al catálogo, y viajan
       * SIEMPRE —vacíos incluidos— porque una edición es un PATCH: si
       * desaparecieran al quitar el último, no habría forma de dejar un
       * producto sin promesas. Su TEXTO lo escribe el servidor.
       */
      benefits: readTextItems(values.benefits),
      /* Los costos, igual: enteros o vacíos, nunca a medias. */
      extraCosts: values.extraCosts
        .filter((row) => row.value.trim() !== "")
        .map((row) => ({
          value: row.value.trim(),
          amounts: buildPrices(row.amounts, currencies),
        })),

      prices: buildPrices(values.prices, currencies),
      compareAtPrices:
        Object.keys(compareAtPrices).length > 0 ? compareAtPrices : undefined,
      memberPrices: Object.keys(memberPrices).length > 0 ? memberPrices : undefined,
      /*
       * `promoPrices` no viaja: lo calcula el servidor desde `discount`.
       * Mandarlo desde aquí sería la segunda fuente de la misma rebaja.
       */

      /*
       * Los GRUPOS de casillas viajan SIEMPRE, incluso con todo apagado.
       *
       * Es lo contrario de lo que se hace con el texto —vacío se omite—
       * y la razón es que una edición es un PATCH: lo que no se manda no
       * se toca. Si `compensation` desapareciera al desmarcar la última
       * casilla, desmarcar "cuenta para calificar" no lo desmarcaría en
       * la base; el campo se quedaría en `true` para siempre y no habría
       * forma de apagarlo desde el formulario.
       */
      discount: {
        active: values.discount.active,
        type: values.discount.type as "percent" | "fixed",
        value: typeof discountValue === "number" ? discountValue : 0,
      },
      compensation: {
        commissionPoints: readInteger(values.compensation.commissionPoints) ?? undefined,
        promoVolumePoints:
          readInteger(values.compensation.promoVolumePoints) ?? undefined,
        promoCommissionPoints:
          readInteger(values.compensation.promoCommissionPoints) ?? undefined,
        isQualifying: values.compensation.isQualifying,
        generatesPersonalPV: values.compensation.generatesPersonalPV,
        generatesGroupPV: values.compensation.generatesGroupPV,
        qualifiesForRank: isActive(values.activation, ACTIVATION_OPTIONS.qualifying),
        qualifiesForText: textOrUndefined(values.compensation.qualifiesForText),
        isRepurchase: values.compensation.isRepurchase,
        maintainsActive: values.compensation.maintainsActive,
        membershipBenefitsText: textOrUndefined(
          values.compensation.membershipBenefitsText,
        ),
      },
      /*
       * El VP vive en UN solo sitio: suelto en el producto. Es el campo
       * que lee el motor de comisiones y el que lee el carrito para
       * decir "necesitas 100 VP". La copia que había dentro de
       * `compensation` se fue del contrato: era la misma cifra escrita
       * dos veces desde este mismo control.
       */
      volumePoints: readInteger(values.volumePoints) ?? undefined,

      imageUrl: textOrUndefined(values.imageUrl),
      imageUrls: images.length > 0 ? images : undefined,
      datasheet: {
        url: values.datasheet.url.trim(),
        name: textOrUndefined(values.datasheet.name),
      },
      facts: {
        sku: textOrUndefined(values.facts.sku),
        format: textOrUndefined(values.facts.format),
        servingSize: textOrUndefined(values.facts.servingSize),
        servingsPerContainer: readInteger(values.facts.servingsPerContainer) ?? undefined,
        suggestedUse: textOrUndefined(values.facts.suggestedUse),
        manufacturedIn: textOrUndefined(values.facts.manufacturedIn),
        manufacturedFor: textOrUndefined(values.facts.manufacturedFor),
        certifications: readTextItems(values.facts.certifications),
        ingredients: readTextItems(values.facts.ingredients),
        contraindications: readTextItems(values.facts.contraindications),
      },

      stock: readInteger(values.stock) ?? undefined,
      shipping: {
        requiresShipping: isActive(values.activation, ACTIVATION_OPTIONS.requiresShipping),
        weightGrams: readInteger(values.shipping.weightGrams) ?? undefined,
        lengthCm: readDecimal(values.shipping.lengthCm) ?? undefined,
        widthCm: readDecimal(values.shipping.widthCm) ?? undefined,
        heightCm: readDecimal(values.shipping.heightCm) ?? undefined,
      },
      validity: {
        from: readDate(values.validity.from) ?? undefined,
        to: readDate(values.validity.to) ?? undefined,
        durationDays: readInteger(values.validity.durationDays) ?? undefined,
      },

      isWholesale: values.isWholesale,
      franchiseeDiscountPercent:
        readDecimal(values.franchiseeDiscountPercent) ?? undefined,
      presentation: textOrUndefined(values.presentation),
      dosage: textOrUndefined(values.dosage),
      legend: textOrUndefined(values.legend),

      /*
       * El editorial SÍ se omite cuando no hay nada: no es un grupo de
       * casillas sino contenido, y "no escribí ficha" no necesita
       * guardarse como un objeto vacío. Al editar, en cambio, viaja
       * `{ es: {} }` en cuanto había algo y se borró — que es lo que
       * vacía la ficha en la tienda.
       */
      editorial:
        editorialHasContent(editorialEs) || editorialHasContent(editorialEn)
          ? { es: editorialEs, en: editorialHasContent(editorialEn) ? editorialEn : undefined }
          : undefined,
    };
  })
  .pipe(productInputSchema);

/** Al editar, el slug no viaja: cambiarlo rompería los enlaces ya compartidos. */
export function toProductUpdate(input: ProductInput): ProductUpdate {
  const { slug: _slug, ...rest } = input;
  return rest;
}

// ── Qué idioma va completo ──────────────────────────────────────────

export type LocaleFillState = "complete" | "partial" | "empty";

/**
 * Cómo está cada idioma SIN tener que abrir su pestaña.
 *
 * `partial` es el estado que importa: alguien escribió el eslogan en
 * inglés y se fue sin el nombre. Sin este aviso, el producto se publica
 * y la tienda en inglés enseña un nombre en español al lado de un
 * eslogan en inglés, y nadie se entera hasta que lo ve un cliente.
 */
export function localeFillState(content: ProductContentFormValues): LocaleFillState {
  const filled = [
    content.name,
    content.tagline,
    content.description,
    content.disclaimer,
  ].filter((value) => value.trim() !== "").length;
  if (filled === 0) return "empty";
  return content.name.trim().length >= 2 ? "complete" : "partial";
}

/** Lo mismo, para el editorial: ¿hay algo escrito en este idioma? */
export function editorialFillState(
  values: EditorialFormValues,
  currencies: readonly CurrencyRowValues[],
): LocaleFillState {
  const built = buildEditorial(values, activeCurrencies(currencies));
  return editorialHasContent(built) ? "complete" : "empty";
}

/** La sugerencia de slug a partir del nombre en español. */
export function suggestSlug(name: string): string {
  return slugify(name);
}

/** Lo que se enseña en la tabla y en la vista previa del formulario. */
export function displayPrice(cents: number | undefined, currency: string): string {
  if (cents === undefined) return "—";
  return formatPrice(cents, currency as "MXN" | "USD");
}
